// Copyright 2019-2020 Fastcastcity Inc. All Rights Reserved.

/**
 * @fileoverview Interface with functions that integrate with ESRI modules
 *
 * @author sabir.shrestha@gmail.com (Sabir Shrestha)
 * @author jpl@geodesy.net (Jean-Paul Lavoie)
 */

const EsriLoader = require('esri-loader');
const FastcastService = require('./fastcast-service');

// Global store the EsriModules once loaded
const EsriModules = {};
// Local instance of the loaded Esri modules
const EsriMap = {};
// Other global stores to share data between exports.
let _parcelFeatureLayer = {};
let _elevationLayer = {};
let _parcelGraphic;
let _outSpatialReference;
let _lastElevation;
let _fanGraphics = {}; // A dictionary of fan polygons for the current parcel indexed by height.
let _parcelClickEnabled = true; // Psuedo property enabling/disabling clicks on the parcel map.
const _eventHandlers = {}; // A dictionary of event handling functions keyed by the event name.

const EVENT_TYPES = {
    UPDATE_ELEVATION: 'gotElevation',
    UPDATE_PARCEL: 'gotParcel'
};

const api = {
    fastcastService: new FastcastService()
};

const EsriService = {
    /**
     * Load Esri modules and load them into a service global variable EsriModules
     *
     * @return {Promise}  Returns a promise.
     */
    loadEsriModules: () => {
        return EsriLoader.loadModules([
            'esri/views/MapView',
            'esri/WebMap',
            'esri/widgets/Search',
            'esri/widgets/ScaleBar',
            'esri/layers/ElevationLayer',
            'esri/core/urlUtils',
            'esri/geometry/SpatialReference',
            'esri/geometry/projection',
            'esri/layers/FeatureLayer',
            'esri/layers/GraphicsLayer',
            'esri/Graphic'
        ]).then((modules) => {
            EsriModules.MapView = modules[0];
            EsriModules.WebMap = modules[1];
            EsriModules.Search = modules[2];
            EsriModules.ScaleBar = modules[3];
            EsriModules.ElevationLayer = modules[4];
            EsriModules.UrlUtils = modules[5];
            EsriModules.SpatialReference = modules[6];
            EsriModules.Projection = modules[7];
            EsriModules.FeatureLayer = modules[8];
            EsriModules.GraphicsLayer = modules[9];
            EsriModules.Graphic = modules[10];
        }).catch((error) => {
            console.warn('Error in loading ESRI modules', error);
        });
    },

    /**
     * Render Esri map in the referred div
     *
     * @param {String} containerId   The div container for the map.  Typically 'mapDiv'.
     *
     */
    renderMapView: (containerId) => {
        // Setup the tin model for parcel elevation queries.
        _elevationLayer = new EsriModules.ElevationLayer({
            url: 'https://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer/'
        });

        // Setup a feature layer for server-side parcel queries (on click).
        _parcelFeatureLayer = new EsriModules.FeatureLayer({
            url: 'https://services8.arcgis.com/SeelVoY3qN5dU7fD/arcgis/rest/services/Parcels___Active_and_Retired/FeatureServer/0'
        });

        // Setup the projection for output:  SF state plan (CA Zone 3)
        _outSpatialReference = new EsriModules.SpatialReference({wkid: 2227});

        EsriMap.webmap = new EsriModules.WebMap({
            portalItem: {
                // id: 'dddeb58cfb3d4c6a819dd8330154a2d4' // geodesysf.map.arcgis.com  'SampleParcelPubTest' web map (SHARED).
                id: 'c19fdcf9403847cfac2f5f6fc4d7133c' // fastcastcity.maps.arcgis.com  'FastcastcityAppMap' web map (SHARED).
            }
        });

        EsriMap.view = new EsriModules.MapView({
            map: EsriMap.webmap,
            container: containerId
        });

        // Add the layer used to draw parcels returned from server-side query as temporary graphics.
        EsriMap.parcelGraphicsLayer = new EsriModules.GraphicsLayer();
        EsriMap.webmap.add(EsriMap.parcelGraphicsLayer);

        // Add the layer used to draw shadow fans returned from the solar engine as temporary graphics.
        EsriMap.fanGraphicsLayer = new EsriModules.GraphicsLayer();
        EsriMap.webmap.add(EsriMap.fanGraphicsLayer);

        // Add the layer used to draw net-new park polygons returned from the solar engine as temporary graphics.
        EsriMap.netNewParksLayer = new EsriModules.GraphicsLayer();
        EsriMap.webmap.add(EsriMap.netNewParksLayer);

        // Add the layer used to draw net-new polygons returned from the solar engine as temporary graphics.
        EsriMap.netNewPolygonsLayer = new EsriModules.GraphicsLayer();
        EsriMap.webmap.add(EsriMap.netNewPolygonsLayer);

        // Add the search widget.
        EsriMap.searchWidget = new EsriModules.Search({
            view: EsriMap.view
        });

        // Add the scale bar widget.
        EsriMap.scaleBar = new EsriModules.ScaleBar({
            view: EsriMap.view,
            style: 'ruler'
        });

        // Arrange (position) the tools.
        EsriMap.view.ui.add(EsriMap.searchWidget, 'top-left');
        EsriMap.view.ui.move('zoom', 'top-left');
        EsriMap.view.ui.add(EsriMap.scaleBar, {position: 'bottom-left'});

        // Respond to a click on the map (a point)
        // by looking for a parcel at that point
        // and yielding the elevation of the parcel's centroid.
        EsriMap.view.on('click', (event) => {
            //  alert('map click');
            // Get the elevation of the clicked point (not the parcel centroid).
            //  getElevation(event.mapPoint);
            // Skip this event if parcel clicking is disabled.  Added 191015.
            if (!_parcelClickEnabled) return;
            // Clear all of the parcel and fan temporary graphics.
            EsriService.clearMapGraphics();
            // Get the map click and take action.
            EsriMap.view.hitTest(event)
                .then((response) => {
                    if (response.results[0] && response.results[0].graphic) {
                        const graphic = response.results[0].graphic;
                        if (graphic.layer.title === 'Parcels___Active_and_Retired') {
                            //  log('@parcel-click...', graphic);
                            memorizeClickedParcel(graphic);
                        } else noVisibleParcelClicked(event.mapPoint);
                    } else noVisibleParcelClicked(event.mapPoint);
                });
        });

        // Respond to a click on a popup action button
        // like the Download button on the net-new park pop-up.
        EsriMap.view.when(() => {
            const popup = EsriMap.view.popup;
            popup.viewModel.on('trigger-action', (event) => {
                if (event.action.id === 'download-csv') {
                    // Download the park's csv data to the user's browser.
                    // When they click the download, Excel will open (if they have it).
                    const attributes = popup.viewModel.selectedFeature.attributes;
                    const hiddenElement = document.createElement('a');
                    hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(attributes.csvData);
                    hiddenElement.target = '_blank';
                    const parkName = attributes.ParkName.replace(/\s/g, ''); // Remove spaces from ParkName attribute string.
                    hiddenElement.download = parkName + '-Parcel' + attributes.ParcelId + '-' + attributes.Height + 'ft.csv';
                    hiddenElement.click();
                }
            });
        });
    },

    /**
     * Attach a local function to an esri-service lib event.
     *
     * @param {String} eventName           An esri-service.js event name, e.g. 'gotParcel', 'gotElevation'.
     * @param {function} listenerFunction  A function local to the calling code.
     *
     * @version 191022 JPL - New.
     */
    attachEventListener: (eventName, listenerFunction) => {
        _eventHandlers[eventName] = listenerFunction;
    },

    /**
     * Enables (when true is sent) or disables parcel clicks on the map.
     * Defaults to true (allows parcel clicks).
     *
     * @param {Boolean} enable    true to enable parcel clicks, false to disable.
     *                            Send as null or nothing (no value) to just return the current setting.
     *
     * @return {Boolean}          Current value of the setting.
     *
     * @version 191022 JPL - Converted from exported lib property to exported method.
     */
    parcelClickEnabled: (enable) => {
        if (enable === null || enable === undefined) {
            return _parcelClickEnabled;
        }
        _parcelClickEnabled = (enable) ? true : false;
        return _parcelClickEnabled;
    },

    /**
     * Sets the string display in the search widget at the top of the map.
     *
     * @param {String} searchTerm    A string to display in the search box.
     *                               Typically a street address.
     *
     * @version 191117 JPL - New.
     */
    displaySearchTerm: (searchTerm) => {
        if (searchTerm === null || searchTerm === undefined) {
            searchTerm = '';
        }
        EsriMap.searchWidget.searchTerm = searchTerm;
    },

    /**
     * Make the given parcel current for future solar fan calculations.
     * Zoom the map to the parcel and highlight it.
     *
     * @param {String} parcelId    A city-blessed parcel ID like '3735060'.
     *
     * @return {Promise}
     *
     * @version 191015 JPL - New.
     */
    makeParcelCurrent: (parcelId) => {
        //  log('@makeParcelCurrent: parcelId=' + parcelId);
        // Clear all of the parcel and fan temporary graphics.
        EsriService.clearMapGraphics();
        // Query (server-side) the parcel layer for the given parcel id.
        // If a parcel is found , this function
        // displays emits the parcel ID and the ground elevation at the parcel centroid.
        return queryParcelFeatureLayerBySql('blklot = \'' + parcelId + '\'').then(() => {
            // Show the no-parcel-clicked elevation at the mapPoint (cursor) location.
            // It could be that this elevation will be quickly replaced by the slower-running
            // call to queryParcelFeatureLayerAtPoint() just above (and that is fine).
            if (!_parcelGraphic) {
                if (_eventHandlers[EVENT_TYPES.UPDATE_PARCEL]) _eventHandlers[EVENT_TYPES.UPDATE_PARCEL]({});
            }
        });
    },

    /**
     * Clear the parcel highlight and shadow fan graphics,
     * as well as the net-new graphics.
     *
     * @version 191022 JPL - New.
     * @version 200212 JPL - Added netNew clearing.
     */
    clearMapGraphics: () => {
        EsriMap.parcelGraphicsLayer && EsriMap.parcelGraphicsLayer.removeAll();
        EsriMap.fanGraphicsLayer && EsriMap.fanGraphicsLayer.removeAll();
        _fanGraphics = {};
        _parcelGraphic = null; // Unset the memorized parcel, if there is one.
        EsriService.clearNetNewMapGraphics();
    },

    /**
     * Clear the net-new graphics.
     *
     * @version 200212 JPL - Seeded from clearMapGraphics().
     */
    clearNetNewMapGraphics: () => {
        EsriMap.netNewParksLayer && EsriMap.netNewParksLayer.removeAll();
        EsriMap.netNewPolygonsLayer && EsriMap.netNewPolygonsLayer.removeAll();
    },

    /**
     * Generate and display a shadow fan for a previously clicked parcel
     * and the given height (eg '700').
     *
     * @param {String} accessToken     Optional access token.
     *                                 If not provided, one is generated from super-admin user (for testing).
     * @param {String} cbk             Optional calculation business key for the given user.
     *                                 If not provided, an existing super-admin business key is used
     *                                 by default (for testing).
     * @param {String} height          The height of the envelope.  e.g. '200', '300', ..., '700'.
     * @param {Boolean} haveParcelDxf  Typically False to generate the DXF.
     *                                 True if the parcel DXF has been previously generated and stored
     *                                 (saves processing).
     * @param {String} existingFanDxf  Typically blank ('') to generate and store the fan DXF.
     *                                 A previously generate fan DXF otherwise (to save processing).
     *
     * @version 191017 JPL - Callback args removed.  Switch to use of promises internally.
     */
    createParcelFan: (accessToken, cbk, height, haveParcelDxf, existingFanDxf) => {
        //  log('@createParcelFan: height=' + height);

        let businessKey = ''; // A calculation businessKey returned from the fastcast service for this specific fan calculation.

        Promise.resolve().then(() => {
            // If the requested parcel fan graphic was already created for the
            // current parcel, just ensure it is visible.
            if (_fanGraphics[height]) {
                _fanGraphics[height].visible = true;
                return Promise.reject('Existing fan graphic displayed.');
            }

            // Return if there is no current parcel selected.
            if (!_parcelGraphic || !_parcelGraphic.attributes || !_parcelGraphic.attributes.blklot) {
                reportStatus('No parcel selected.');
                return Promise.reject('No parcel selected.');
            }

            // Generate the parcel fan.

            reportStatus('Generating Shadow Fan', '1. Authorizing solar engine...');
            if (accessToken) {
                // Use the access token provided to this function.
                return Promise.resolve({'data': {'access_token': accessToken}});
            } else {
                // Get an access token for a fixed user (for testing).
                return api.fastcastService.getToken('super-admin', 'password');
            }
        }).then((response) => {
            // Here with an access token in response.data.access_token (either
            // the one provided to this function or a new one).
            if (!response.data || !response.data.access_token) {
                reportStatus('ERROR: No access_token.');
                return Promise.reject('ERROR: No access_token.');
            }
            accessToken = response.data.access_token;
            // Update the existing 'FanDemo' calculation businessKey for test-client-1,
            // test-project1 with the new height.
            // NOTE that this adds the parcel's centroid elevation (from a Esri tin model)
            // to the requested extrusion height.
            reportStatus('Generating Shadow Fan', '2. Setting up the fan calculation...');
            if (!cbk) cbk = '521e913a-2168-4e7e-ac5b-dde3d5be1714'; // Use a default calc business key (one from the super-admin user) if none was provided.
            const ht = parseInt(height) + _lastElevation;
            return api.fastcastService.updateCalculationDxf(accessToken, cbk, 'FanDemo', ht);
        }).then((response) => {
            // Here with a business key in response.businessKey.  (It is used below.)
            if (!response || !response.data || !response.data.businessKey) {
                reportStatus('ERROR: No calculation businessKey.');
                return Promise.reject('ERROR: No calculation businessKey.');
            }
            businessKey = response.data.businessKey;
            // Generate the DXF for the current parcel (as a string),
            // unless it was already stored on the fastcast server.
            // NOTE that the resulting DXF includes the parcel's
            // centroid elevation (_lastElevation) as the z values.
            reportStatus('Generating Shadow Fan', '3. Generating the parcel\'s DXF...');
            if (haveParcelDxf) {
                // Use the dxf that was previously generated for this parcel
                // and stored on the fastcast server.
                return Promise.resolve('(DXF already calculated and stored)');
            } else {
                // Generate a DXF string of the current parcel.  (It comes back in response.)
                return EsriService.getParcelDxf(_lastElevation);
            }
        }).then((response) => {
            // Make sure there is a returned parcel DXF string (or a message string).
            if (!response) {
                reportStatus('ERROR: No parcel dxf.');
                return Promise.reject('ERROR: No parcel dxf.');
            }
            if (response.startsWith('ERROR:')) {
                reportStatus(response);
                return Promise.reject(response);
            }
            const parcelDxfString = response;
            // Upload the parcel DXF to the calculation,
            // unless it was already stored on the fastcast server.
            reportStatus('Generating Shadow Fan', '4. Posting the parcel\'s DXF to the solar engine...');
            if (haveParcelDxf) {
                // Use the dxf that was previously generated for this parcel
                // and stored on the fastcast server (a nice processing shortcut).
                return Promise.resolve('(DXF already calculated and stored)');
            } else {
                // Store the parcel DXF string on the fastcast server.
                return api.fastcastService.postParcelDxf(accessToken, businessKey, parcelDxfString);
            }
        }).then((response) => {
            // Compute the solar fan,
            // unless it was already computed and stored on the fastcast server.
            reportStatus('Generating Shadow Fan', '5. Computing the shadow fan...');
            if (existingFanDxf) {
                // Use the fan dxf that was previously generated and provided.
                // (This is another processing shortcut.)
                return Promise.resolve('(Fan DXF already computed)');
            } else {
                // Have the fastcast server compute the fan DXF.
                return api.fastcastService.computeCalculation(accessToken, businessKey);
            }
        }).then((response) => {
            // Get the new solar fan's DXF,
            // unless it was already provided (i.e. previously generated).
            reportStatus('Generating Shadow Fan', '6. Retrieving the shadow fan DXF from the solar engine...');
            if (existingFanDxf) {
                // Use the fan dxf that was previously generated and provided.
                // (This is another processing shortcut.)
                return Promise.resolve(existingFanDxf);
            } else {
                // Have the fastcast server compute the fan DXF.
                return api.fastcastService.getCalculationDxf(accessToken, businessKey);
            }
        }).then((response) => {
            // Make sure there is a returned fan DXF string in the response.
            // (Note that the DXF string may be the one previously calculated
            // and provided to this function.)
            if (!response || !response.data) {
                reportStatus('ERROR: No fan dxf.');
                return Promise.reject('ERROR: No fan dxf.');
            }
            // Convert the fan dxf to agol json.
            reportStatus('Generating Shadow Fan', '7. Converting the shadow fan DXF to AGOL JSON...');
            const fanJson = EsriService.convertDxfToAgolJson(response.data, {Desc2: 'TestFan' + height});
            if (fanJson.startsWith('ERROR:')) {
                reportStatus(fanJson);
                return Promise.reject(fanJson);
            }
            // Here with json fan geometry for agol.
            // Display the fan as temporary graphics on the map.
            reportStatus('Generating Shadow Fan', '8. Displaying the shadow fan...');
            return EsriService.displayPolygon(fanJson, height, 'fan');
        }).then((response) => {
            reportStatus('Generating Shadow Fan', '9. Done!');
            reportStatus();
            return 'Complete.';
        });
    },

    /* --------------------------------------------------------------------------
        DXF functions.
    -------------------------------------------------------------------------- */

    /**
     * Get the DXF of the most recently clicked parcel as a simple,
     * single POLYLINE, similar to the 3735060_minA.dxf test sample.
     * The returned string is suitable for sending to the solar service.
     * It contains coordinates projected into SF state plane (srid=2227).
     *
     * @param {String} parcelCentroidHeight  If provided, this vertical ht in ft
     *                                       is used as DXF Z (30 code) values.
     *
     * @return {Promise<String>}  A DXF string is returned in the response on success.
     *
     * @version 190709 JPL - New.
     * @version 190711 JPL - Added parcelCentroidHeight arg.
     * @version 190930 JPL - Added haveParcelDxf arg.
     * @version 191017 JPL - Removed callback function and haveParcelDxf args.  Convert to Promise.
     */
    getParcelDxf: (parcelCentroidHeight) => {
        let z = 0;

        // Return if no parcel has been clicked.
        if (!_parcelGraphic) {
            reportStatus('ERROR: No parcel has been clicked.');
            return Promise.reject('ERROR: No parcel has been clicked.');
        }
        if (parcelCentroidHeight) z = parcelCentroidHeight;

        // Return if the parcel does not have adequate geometry.
        if (!_parcelGraphic.geometry || !_parcelGraphic.geometry.rings
            || !_parcelGraphic.geometry.rings[0] || _parcelGraphic.geometry.rings[0].length < 3) {
            reportStatus('ERROR: The selected parcel is not a polygon.');
            return Promise.reject('ERROR: The selected parcel is not a polygon.');
        }

        // Load the ArcGIS Projection module.
        // This is fast if it has already been loaded.
        return EsriModules.Projection.load().then(() => {
            // Re-project the parcel polygon into SF state plane.
            const parcelGeometry = EsriModules.Projection.project(_parcelGraphic.geometry, _outSpatialReference);
            // Start the DXF output string.
            let dxf = '0\nSECTION\n2\nENTITIES\n0\nPOLYLINE\n8\nParcel\n66\n1\n70\n1\n10\n0\n20\n0\n30\n0\n';
            // Loop through the vertexes in the first parcel ring to create the DXF POLYLINE.
            for (let i = 0, len = parcelGeometry.rings[0].length; i < len; i++) {
                const vert = parcelGeometry.rings[0][i];
                dxf += '0\nVERTEX\n8\nParcel\n10\n' + vert[0] + '\n20\n' + vert[1] + '\n30\n' + z + '\n';
            }
            // Conclude the DXF output string.
            dxf += '0\nSEQEND\n8\nParcel\n0\nENDSEC\n0\nEOF';
            // All done.
            return Promise.resolve(dxf);
        });
    },

    /**
     * Convert a given solar fan or net-new DXF string to AGOL json geometry format.
     * <br/>Return the json on success, or a string starting with 'ERROR:' on failure.
     *
     * @param {String} dxfString  Solar fan or net-new DXF string to convert.
     * @param {Object} attrs      An optional dictionary of attribute key-values to store,
     *                            e.g. { Desc2: "{layerName}", ParcelId: "3456789", Height: "700" }.
     *                            The Desc2 attribute is a description of the solar fan
     *                            or net-new polyline to invlude in the output AGOL json.
     *                            This can include '{layerName}' to embed the polyline's
     *                            layer name (group code 8) in the returned Desc2 value.
     *                            The other attributes are typically used to populate
     *                            the popup contents.
     * @param {Number} polylinesToSkip  Optional.  The number of polylines to skip
     *                            before reading a polygon into json.
     *                            This allows this function to be called multiple
     *                            times to read all polylines into json.
     *                            Defaults to 0 (i.e. read the first dxf polyline).
     *
     * @return {String}  The AGOL json polyline geometry,
     *                   or a blank string if a polyline was not found (taking
     *                   into account polylinesToSkip),
     *                   or an error message starting with 'ERROR:'.
     *
     * @version 190710 JPL - Divided out from LoadDxf().
     * @version 200212 JPL - Added optional polylinesToSkip arg to accommodate net-new
     *                       dxf files (in addition to fan dxf files).
     *                       If descr contains '{layerName}' use the polyline's layer name
     *                       in the returned json's Desc2 value.
     * @version 200214 JPL - Changed "descr" arg to "attrs", an optional dictionary of
     *                       attribute key value pairs.
     *
     */
    convertDxfToAgolJson: (dxfString, attrs, polylinesToSkip) => {
        let json = '{"geometry":{"rings":[[';
        let polylineStarted = false;
        let firstVertexAdded = false;
        let expectNext = ''; // eg '10' to expect a 10 directive next.
        let polylineName = ''; // eg 'Fan700' or (for net-new) 'Park' or 'NetNew'.
        let curPrim = ''; // eg POLYLINE or VERTEX
        let curX = ''; // dval when dcod='10'
        let prevXy = ''; // to skip repeated verts
        let polylinesFound = 0; // the number POLYLINE group codes found so far.

        if (!attrs) attrs = {}; // Default to no attributes.
        if (!polylinesToSkip) polylinesToSkip = 0; // Default to reading and returning the first polyline.

        const lines = dxfString.split('\n');

        for (let i = 0, len = lines.length; i < len; i = i + 2) {
            const dcod = lines[i].trim();
            if ($.inArray(dcod, ['0', '8', '10', '20']) === -1) continue; // skip non-fan directives
            const dval = lines[i + 1].trim();
            //  if (i < 10) log(dcod + ' : ' + dval);

            if (expectNext !== '' && dcod !== expectNext) {
                return 'ERROR: Expected DXF directive ' + expectNext + ' at line ' + (i + 1) + '.';
            }

            if (!polylineStarted && dcod === '0' && dval === 'POLYLINE') {
                if (polylinesFound === polylinesToSkip) {
                    // Here with the correct polyline to read, convert, and return.
                    polylineStarted = true;
                    expectNext = '8';
                }
                polylinesFound++;
                continue;
            }

            if (polylineStarted && dcod === '0' && dval === 'VERTEX') {
                curPrim = dval; // typ POLYINE or VERTEX
                expectNext = '8';
                continue;
            }

            if (polylineStarted && dcod === '0' && dval !== 'SEQEND') {
                return 'ERROR: Expected VERTEX at line ' + (i + 2) + '.';
            }

            if (polylineStarted && dcod === '8' && polylineName === '') {
                polylineName = dval;
                expectNext = '';
                continue;
            }

            if (polylineStarted && polylineName !== '' && dcod === '8') {
                expectNext = '10';
                continue;
            }

            if (polylineStarted && dcod === '10' && curPrim === 'VERTEX') {
                curX = dval;
                expectNext = '20';
                continue;
            }

            if (polylineStarted && dcod === '20' && curPrim === 'VERTEX') {
                expectNext = '0';
                const curY = dval;
                const curXy = curX + ',' + curY;
                if (curXy === prevXy) continue; // Skip repeated vertex.
                prevXy = curXy;
                //  log('xy: ' + curX + ',' + dval);
                // Here with a X and Y.  Add it to the json.
                if (firstVertexAdded) json += ','; else firstVertexAdded = true;
                json = json + '\n[' + curXy + ']';
                continue;
            }

            if (polylineStarted && dcod === '0' && dval === 'SEQEND') {
                // Here with the xy values read and success.  Close out and return the json,
                // including any optional polygon attributes (like ParcelId and TaasPercent).
                json += '\n]]}';
                if (Object.keys(attrs).length > 0) {
                    json += ',"attributes":{';
                    for (const key in attrs) {
                        if (attrs.hasOwnProperty(key)) {
                            if (!json.endsWith('{')) json += ', ';
                            let val = attrs[key];
                            if (typeof val === 'string') {
                                val = val.replace('{layerName}', polylineName);
                            }
                            json += '"' + key + '": "' + val + '"';
                        }
                    }
                    json += '}}';
                } else {
                    json += '}';
                }
                return json;
            }
        }
        // Return blank if a polyline (taking polylinesToSkip into account)
        // wasn't found.
        if (!polylineStarted) return '';
        return 'ERROR: Reached end of DXF without a POLYLINE SEQEND at line.';
    },

    /* --------------------------------------------------------------------------
        CSV functions.
    -------------------------------------------------------------------------- */

    /**
     * Calc the TAAS percentage from a given CSV file contents string.
     * The formula used is
     *   TAAS % = (TotalProposedNewSfHrShadow * 13.50143495) / (ParkArea * 3721.4)
     *
     * @param {String} csv  The text from a csv file that contains lines starting
     *                      with "ParkArea," and "TotalProposedNewSfHrShadow,".
     *
     * @return {String}  The calculated TAAS, times 100 (for %) and
     *                   formatted as "n.nnn%".
     *
     * @version 200214 JPL - New.
     */
    calcTaasFromCsv: (csv) => {
        let tpnsfhrs = 0;
        let parkArea = 0;

        const lines = csv.split('\n');

        for (let i = 0, len = lines.length; i < len; i++) {
            const line = lines[i].trim();
            if (line.startsWith('ParkArea,')) {
                parkArea = parseFloat(line.split(',')[1]);
            }
            if (line.startsWith('TotalProposedNewSfHrShadow,')) {
                tpnsfhrs = parseFloat(line.split(',')[1]);
            }
        }

        if (parkArea === 0) return '0.000%';
        const taas =(tpnsfhrs * 13.50143495) / (parkArea * 3721.4);
        return (taas * 100).toFixed(3) + '%';
    },

    /* --------------------------------------------------------------------------
        Polygon dislay functions.
    -------------------------------------------------------------------------- */

    /**
     * Add a styled solar fan, park, or net-new polygon to the current map
     * (from the rings member of the given json string).
     * If the polygon is a fan, zoom to the polygon.
     *
     * @param {String} jsonStr  Json containing rings member.
     * @param {String} height   The height of the solar fan (for indexing the graphic), e.g. '700'.
     * @param {String} type     'fan', 'park', or 'net-new'.  Defaults to 'fan'.
     * @param {String} csvStr   Optional.  A string of csv data.  Include for type 'park'.
     *
     * @version 190719 JPL - New.
     * @version 190927 JPL - Reverse the fan polygon if it is counter-clockwise (i.e. a hole).
     * @version 191016 JPL - Add the height arg.  If the given solar fan (by height) is already
     *                       a graphic, just make ensure it is visible.
     * @version 191018 JPL - Remove callback arg.  Convert to Promise.
     * @version 191022 JPL - Renamed from displayPolygon displayFanPolygon.
     * @version 200202 JPL - Change fan zoom-out from "- 1" to "- 0.25".
     * @version 200212 JPL - Renamed from displayFanPolygon back to displayPolygon.
     *                       Added type arg.
     * @version 200213 JPL - Added popup (pop-up) for net-new park polygon graphics.
     * @version 200215 JPL - Added optional csvStr arg for net-new popup download.
     */
    displayPolygon: (jsonStr, height, type, csvStr) => {
        if (!type) type = 'fan';
        Promise.resolve().then(() => {
            // If the requested graphic is a fan and was already created for the
            // current parcel, just ensure it is visible.
            if (type === 'fan' && _fanGraphics[height]) {
                _fanGraphics[height].visible = true;
                return;
            }
            // Convert the polygon geometry (a ring) from a json string to json.
            const rings = JSON.parse(jsonStr).geometry.rings;
            // Create an esri polygon out of the json ring.
            const polygon = {
                type: 'polygon',
                rings: rings,
                spatialReference: _outSpatialReference
            };
            // Reverse the ring if it is drawn backward, as a hole.
            const pg = new EsriModules.Graphic({geometry: polygon});
            const isCw = pg.geometry.isClockwise(pg.geometry.rings[0]);
            if (!isCw) {
                // Reverse the json array of vertexes to turn the ring from a hole to a fillable polygon ring.
                rings[0].reverse();
                polygon.rings = rings;
            }
            // Create a symbol (a style) for the display of the polygon.
            let simpleFillSymbol = {};
            if (type === 'park') {
                simpleFillSymbol = {
                    type: 'simple-fill',
                    color: [114, 194, 138, 0.7], // green, opacity 70%
                    outline: {
                        color: [255, 255, 255], // white
                        width: 1
                    }
                };
            } else if (type === 'net-new') {
                simpleFillSymbol = {
                    type: 'simple-fill',
                    color: [178, 86, 170, 0.8], // purple, opacity 80%
                    outline: {
                        color: [178, 86, 170], // purple
                        width: 0
                    }
                };
            } else { // 'fan'
                simpleFillSymbol = {
                    type: 'simple-fill',
                    color: [227, 139, 79, 0.4], // orange, opacity 40%
                    outline: {
                        color: [255, 255, 255], // white
                        width: 1
                    }
                };
            }
            // Transform the polygon into the map's projection and display it.
            EsriModules.Projection.load().then(() => {
                // Do the projection of the polygon geometry.
                const polygon2 = EsriModules.Projection.project(polygon, EsriMap.view.spatialReference);
                const polygonGraphic = new EsriModules.Graphic({
                    geometry: polygon2,
                    attributes: JSON.parse(jsonStr).attributes,
                    symbol: simpleFillSymbol
                });
                // Add the fan graphic to an array of the parcel's fans for later turning off-on.
                if (type === 'fan') {
                    _fanGraphics[height] = polygonGraphic;
                }
                // Place the polygon graphic on the map.
                // If the polygon is a net-new park, add a popup.
                if (type === 'park') {
                    const downloadCsvAction = {
                        title: 'Download park\'s shadow data',
                        id: 'download-csv',
                        className: 'esri-icon-download' // Sets the icon font used to style the action button.
                    };
                    polygonGraphic.popupTemplate = {
                        title: '<b>{ParkName}</b>',
                        content: 'Parcel {ParcelId} at {Height} ft.<br>Proposal % of TAAS: <b>{TaasPercent}</b>',
                        outFields: ['*'],
                        overwriteActions: true, // Turn off the zoom-to tool.
                        actions: [downloadCsvAction]
                    };
                    polygonGraphic.attributes.csvData = csvStr; // Add a mock attr to pass csv to the popup handler.
                    EsriMap.netNewParksLayer.add(polygonGraphic);
                } else if (type === 'net-new') {
                    EsriMap.netNewPolygonsLayer.add(polygonGraphic);
                } else { // 'fan'
                    EsriMap.fanGraphicsLayer.add(polygonGraphic);
                }
                // Zoom the map to the polygon (with a margin around it)
                // if it is a fan.
                if (type === 'fan') {
                    EsriMap.view.goTo(polygonGraphic).then(() => {
                        EsriMap.view.zoom = EsriMap.view.zoom - 0.25; // was "- 1" prior to 200202.
                        return;
                    });
                } else return;
            });
        });
    },

    /**
     * Display any number of styled polygons on the current map
     * (from the rings member of the given polygon json strings).
     * This calls displayPolygon for each jsonStr/height pair
     * in the given array.
     * >> TEST THIS
     *
     * @param {Array} jsonStrHeightArray  Array of polygon jsonStr and height pairs (e.g. '700').
     * @param {String} type               'fan', 'park', or 'net-new'.
     *
     * @version 191022 JPL - New.
     * @version 191022 JPL - Renamed from displayFanPolygons to displayPolygons.
     *                       Added type arg.
     */
    displayPolygons: (jsonStrHeightArray, type) => {
        jsonStrHeightArray.forEach((element) => {
            EsriService.displayPolygon(element.jsonStr, element.height, type);
        });
    },

    /**
     * Turn the given parcel fan graphic on or off on the dislay.
     * If the given parcel fan (the given height) does not exist, this function does nothing.
     *
     * @param {String} height    The height of the existing fan graphic to display, e.g. '700'.
     * @param {Boolean} visible  Send as true to make the fan graphic visible on the map,
     *                           false to hide it.
     *
     * @return {Boolean}         Resulting visibility of the fan graphic.  This also returns false
     *                           if a fan of the given height has not yet been generated by createParcelFan.
     *
     * @version 191015 JPL - New.
     */
    makeFanGraphicVisible: (height, visible) => {
        //  log('@makeFanGraphicVisible: height=' + height);
        if (_fanGraphics[height]) {
            _fanGraphics[height].visible = visible;
            return _fanGraphics[height].visible;
        } else {
            return false;
        }
    },

    /**
     * Display a set of park and net-new polygons on the map.
     * This starts by turning the given array of DXF strings (which each typ contain
     * one 'Park' and one or more 'NetNew' polyline layer names)
     * into a set of Agol json graphics.
     *
     * @param {string} parcelId     The ID/name of the subject parcel, e.g. '3735060'.
     * @param {string} height       The extusion height of the existing graphic to display, e.g. '700'.
     * @param {Object[]} dxfAndCsv  An array of dxf and csv file strings that describe the net-new shadow
     *                              for the given parcel and extrusion height.
     *                              This is in the form
     *                                 [{parkName: $parkName, dxfData: $dxfPayload, csvData: $csvData}]
     *                              where each array entry is a park that receives net-new shadow
     *                              and where the dxf contains the park polygon (in the 'Park' layer)
     *                              and one or more net-new shadow polygons (in the 'NetNew' layer).
     *
     * @version 200212 JPL - New.
     * @version 200214 JPL - Supply list of attrs to convertDxfToAgolJson.
     */
    renderNetNew: (parcelId, height, dxfAndCsv) => {
        //  log('@renderNetNew: height=' + height);
        // Remove any currently-displayed net-new graphics from the map.
        EsriService.clearNetNewMapGraphics();
        // Read the park and each net-new polygon from each given dxf,
        // turn it into Agol json, and display it on the map.
        for (let i = 0, len = dxfAndCsv.length; i < len; i++) { // Loop through the parks with shadows.
            const dxfStr = dxfAndCsv[i].dxfData;
            const csvStr = dxfAndCsv[i].csvData;
            const taasPercent = EsriService.calcTaasFromCsv(csvStr);
            let polylinesToSkip = 0;
            const attrs = {
                Desc2: '{layerName}' + height,
                ParcelId: parcelId,
                Height: height,
                ParkName: dxfAndCsv[i].parkName,
                TaasPercent: taasPercent
            };
            let polygonJson = EsriService.convertDxfToAgolJson(dxfStr, attrs, polylinesToSkip);
            while (polygonJson) {
                // Display the polygonJson polygon on the map.
                let type = 'net-new';
                const desc2 = JSON.parse(polygonJson).attributes.Desc2;
                if (desc2 === 'Park' + height) type = 'park';
                EsriService.displayPolygon(polygonJson, height, type, csvStr);
                // Get the next park or net-new polygon for this park.
                polygonJson = EsriService.convertDxfToAgolJson(dxfStr, attrs, ++polylinesToSkip);
            }
        }
    }
};

/* --------------------------------------------------------------------------
    Functions to handle parcel clicks.
-------------------------------------------------------------------------- */

// Define the off-parcel-click response.  This includes clicks when the parcel layer is not displayed.
function noVisibleParcelClicked(mapPoint) {
    // Query (server-side) the parcel layer at the clicked map point.
    // If a parcel is found at the clicked position (mapPoint), this function
    // displays emits the parcel ID and the ground elevation at the parcel centroid.
    queryParcelFeatureLayerAtPoint(mapPoint, 1, 'intersects'); // No projection is needed here (even though parcel layer is in state plane).
    // Show the no-parcel-clicked elevation at the mapPoint (cursor) location.
    if (!_parcelGraphic) {
        if (_eventHandlers[EVENT_TYPES.UPDATE_PARCEL]) _eventHandlers[EVENT_TYPES.UPDATE_PARCEL]({});
        getElevation(mapPoint).then((elevation) => {
        });
    }
}

// Here with a parcel found at the click position.  Memorize it.
function memorizeClickedParcel(graphic) {
    // Get the elevation of the clicked parcel's centroid.
    getElevation(graphic.geometry.centroid).then((centroidHeight) => {
        // Get and report the clicked parcel.
        _parcelGraphic = graphic;
        const parcelId = graphic.attributes.blklot;
        const centroid = graphic.geometry.centroid;
        const streetAddress = graphic.attributes.from_addre + ' ' + graphic.attributes.street_nam + ' ' + graphic.attributes.street_typ;
        if (_eventHandlers[EVENT_TYPES.UPDATE_PARCEL]) {
            _eventHandlers[EVENT_TYPES.UPDATE_PARCEL]({
                parcelId,
                centroidHeight,
                centroidLongitude: centroid.longitude,
                centroidLatitude: centroid.latitude,
                streetAddress
            });
        }
    });
}

/* --------------------------------------------------------------------------
    Functions to handle elevation request clicks.
-------------------------------------------------------------------------- */

// Get an elevation from a position (lon-lat).
// Raise the gotElevation event in the UI.
// 190926 JPL - Change from _mapDivNode.dispatchEvent() to window.gotElevation().
// 191019 JPL - Remove callback arg.  Handle through promise.
// 191022 JPL - Return elevation.
function getElevation(position) {
    //  log('@fagol.getElevation');
    reportStatus('Elevation', '1. Got a click...');
    return _elevationLayer.queryElevation(position).then((results) => {
        // Converts from meters to standard (aka international) feet (/ 0.3048).
        const elevation = results.geometry.z / 0.3048;
        //  log('elevation: ' + elevation);
        reportStatus('Elevation', '2. The elevation is ' + elevation);
        _lastElevation = elevation;
        if (_eventHandlers[EVENT_TYPES.UPDATE_ELEVATION]) _eventHandlers[EVENT_TYPES.UPDATE_ELEVATION](elevation);
        reportStatus('Elevation', '3. Elevation reported via event.');
        return elevation;
    });
}

/* --------------------------------------------------------------------------
    Functions to support querying the parcels layer service,
    and displaying clicked parcels as temporary graphics.
-------------------------------------------------------------------------- */

function addParcelGraphics(feature) {
    const parcelFillSymbol = {
        type: 'simple-fill',
        color: [255, 0, 0, 0.5], // red, opacity 50%
        outline: {
            color: [255, 255, 255],
            width: 1
        }
    };

    EsriMap.parcelGraphicsLayer.removeAll();
    const g = new EsriModules.Graphic({
        geometry: feature.geometry,
        attributes: feature.attributes,
        symbol: parcelFillSymbol
    });
    EsriMap.parcelGraphicsLayer.add(g);
}

function queryParcelFeatureLayerAtPoint(point, distance, spatialRelationship, sqlExpression) {
    const query = {
        geometry: point,
        distance: distance,
        spatialRelationship: spatialRelationship,
        outFields: ['*'],
        returnGeometry: true,
        where: sqlExpression
    };
    return _parcelFeatureLayer.queryFeatures(query).then((result) => {
        if (result && result.features && result.features.length > 0) {
            addParcelGraphics(result.features[0], true);
            memorizeClickedParcel(result.features[0]);
        }
    });
}

function queryParcelFeatureLayerBySql(sqlExpression) {
    const query = {
        outFields: ['*'],
        returnGeometry: true,
        where: sqlExpression
    };
    return _parcelFeatureLayer.queryFeatures(query).then((result) => {
        if (result && result.features && result.features.length > 0) {
            addParcelGraphics(result.features[0], true);
            memorizeClickedParcel(result.features[0]);
        }
    });
}

/* --------------------------------------------------------------------------
    General purpose functions.
-------------------------------------------------------------------------- */

// Send a status message to the UI.
// 191020 JPL - Encapsulated to trap for no handler.
function reportStatus(title, status) {
    if (window && window.statusChange) window.statusChange(title, status);
}

// Send a message to the console log.
// This function encapsulates the console.log calls so they can be easily commented-out.
// Note that IE errors on console.log if the debugger is not active.
// Use msg2 to contain display an object (as opposed to a string or number) in the log.
// 110605 JPL
function log(msg, msg2) {
    if (!console) return;
    console.log(msg);
    if (msg2) console.log(msg2);
}

/* --------------------------------------------------------------------------
    Exports.
-------------------------------------------------------------------------- */

module.exports = {
    loadEsriModules: EsriService.loadEsriModules,
    renderMapView: EsriService.renderMapView,
    attachEventListener: EsriService.attachEventListener,
    parcelClickEnabled: EsriService.parcelClickEnabled,
    makeParcelCurrent: EsriService.makeParcelCurrent,
    clearMapGraphics: EsriService.clearMapGraphics,
    clearNetNewMapGraphics: EsriService.clearNetNewMapGraphics,
    createParcelFan: EsriService.createParcelFan,
    getParcelDxf: EsriService.getParcelDxf,
    convertDxfToAgolJson: EsriService.convertDxfToAgolJson,
    displayPolygon: EsriService.displayPolygon,
    displayPolygons: EsriService.displayPolygons,
    makeFanGraphicVisible: EsriService.makeFanGraphicVisible,
    renderNetNew: EsriService.renderNetNew,
    displaySearchTerm: EsriService.displaySearchTerm,
    calcTaasFromCsv: EsriService.calcTaasFromCsv
};
